import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { streamUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    Eye,
    User,
    Megaphone,
    DollarSign,
    MessageCircle,
    Send,
    Edit3,
    Save,
    X,
    Trash2,
    ArrowUpRight,
} from "lucide-react";

const AVATAR_COLORS = ["bg-vibrantyellow", "bg-vibrantpink", "bg-vibrantblue", "bg-vibrantorange"];

export default function VideoFeedItem({ video: initialVideo, index = 0, onDeleted }) {
    const { user } = useAuth();
    const [video, setVideo] = useState(initialVideo);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(true);
    const [newComment, setNewComment] = useState("");
    const [guestName, setGuestName] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const [error, setError] = useState("");

    const [editing, setEditing] = useState(false);
    const [budget, setBudget] = useState(initialVideo.ad_budget || "");
    const [fee, setFee] = useState(initialVideo.ad_fee || "");
    const [saving, setSaving] = useState(false);

    const isOwner = user && user !== false && user.id === video.owner_id;

    const loadComments = useCallback(async () => {
        setLoadingComments(true);
        try {
            const { data } = await api.get(`/videos/${video.id}/comments`);
            setComments(data);
        } catch {
            setComments([]);
        } finally {
            setLoadingComments(false);
        }
    }, [video.id]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    const onSaveAds = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.patch(`/videos/${video.id}`, { ad_budget: budget, ad_fee: fee });
            setVideo(data);
            setEditing(false);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const onAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        setSubmittingComment(true);
        setError("");
        try {
            const body = { text: newComment.trim() };
            if (!user || user === false) body.name = guestName.trim() || "guest";
            const { data } = await api.post(`/videos/${video.id}/comments`, body);
            setComments((prev) => [data, ...prev]);
            setNewComment("");
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || "Failed to comment");
        } finally {
            setSubmittingComment(false);
        }
    };

    const onDeleteVideo = async () => {
        if (!window.confirm(`Delete "${video.title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/videos/${video.id}`);
            if (onDeleted) onDeleted(video.id);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to delete video");
        }
    };

    const onDeleteComment = async (cid) => {
        try {
            await api.delete(`/comments/${cid}`);
            setComments((prev) => prev.filter((c) => c.id !== cid));
        } catch {}
    };

    const visibleComments = comments.slice(0, 3);

    return (
        <article
            data-testid={`feed-item-${video.id}`}
            className="brutal-card overflow-hidden bg-white"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                {/* Left: Player + Title + Ads */}
                <div className="lg:col-span-7 p-5 lg:p-7 lg:border-r-[3px] lg:border-ink">
                    {/* Player */}
                    <div className="relative w-full aspect-video bg-ink rounded-xl border-[3px] border-ink overflow-hidden shadow-brutal">
                        <video
                            data-testid={`feed-player-${video.id}`}
                            src={streamUrl(video.id)}
                            controls
                            playsInline
                            preload="metadata"
                            className="w-full h-full"
                        />
                    </div>

                    {/* Title + meta */}
                    <div className="mt-4">
                        <div className="flex items-start justify-between gap-3">
                            <Link
                                to={`/video/${video.id}`}
                                data-testid={`feed-title-link-${video.id}`}
                                className="group inline-flex items-start gap-2 flex-1 min-w-0"
                            >
                                <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tighter text-ink leading-tight group-hover:underline decoration-vibrantorange decoration-[3px] underline-offset-4">
                                    {video.title}
                                </h2>
                                <ArrowUpRight className="w-5 h-5 mt-1 text-ink/40 group-hover:text-vibrantorange flex-shrink-0" strokeWidth={2.5} />
                            </Link>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            <div className="flex items-center gap-2">
                                <div className={`w-7 h-7 ${AVATAR_COLORS[index % AVATAR_COLORS.length]} border-2 border-ink rounded-full flex items-center justify-center`}>
                                    <User className="w-3.5 h-3.5 text-ink" strokeWidth={3} />
                                </div>
                                <span className="font-heading font-bold text-sm text-ink">@{video.owner_username}</span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-cream border-2 border-ink rounded-md px-2 py-0.5">
                                <Eye className="w-3.5 h-3.5 text-ink" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs text-ink">{video.views} views</span>
                            </div>
                        </div>
                        {video.description && (
                            <p className="font-body text-sm text-ink/80 mt-3 leading-relaxed line-clamp-3">
                                {video.description}
                            </p>
                        )}
                    </div>

                    {/* Advertising Budget & Fee */}
                    <div data-testid={`feed-ads-${video.id}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                        <div className="bg-vibrantyellow border-[3px] border-ink rounded-2xl p-4 shadow-brutal">
                            <div className="flex items-center gap-1.5 mb-1">
                                <Megaphone className="w-4 h-4 text-ink" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink">Video Uploader Budget</span>
                            </div>
                            {editing ? (
                                <input
                                    data-testid={`feed-budget-input-${video.id}`}
                                    className="brutal-input mt-1 bg-white py-2 text-base"
                                    value={budget}
                                    onChange={(e) => setBudget(e.target.value)}
                                    placeholder="e.g. $500"
                                    maxLength={100}
                                />
                            ) : (
                                <div data-testid={`feed-budget-value-${video.id}`} className="font-heading font-black text-xl text-ink tracking-tight">
                                    {video.ad_budget || <span className="text-ink/40">Not set</span>}
                                </div>
                            )}
                        </div>
                        <div className="bg-vibrantblue text-white border-[3px] border-ink rounded-2xl p-4 shadow-brutal">
                            <div className="flex items-center gap-1.5 mb-1">
                                <DollarSign className="w-4 h-4 text-white" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-white">Payment for Advertising</span>
                            </div>
                            {editing ? (
                                <input
                                    data-testid={`feed-fee-input-${video.id}`}
                                    className="brutal-input mt-1 bg-white text-ink py-2 text-base"
                                    value={fee}
                                    onChange={(e) => setFee(e.target.value)}
                                    placeholder="e.g. $50 / spot"
                                    maxLength={100}
                                />
                            ) : (
                                <div data-testid={`feed-fee-value-${video.id}`} className="font-heading font-black text-xl text-white tracking-tight">
                                    {video.ad_fee || <span className="text-white/50">Not set</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Owner controls */}
                    {isOwner && (
                        <div data-testid={`feed-owner-controls-${video.id}`} className="flex flex-wrap gap-2 mt-4">
                            {editing ? (
                                <>
                                    <button
                                        onClick={onSaveAds}
                                        disabled={saving}
                                        data-testid={`feed-ad-save-${video.id}`}
                                        className="brutal-btn-primary text-xs py-2 px-3"
                                    >
                                        <Save className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        {saving ? "Saving..." : "Save"}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditing(false);
                                            setBudget(video.ad_budget || "");
                                            setFee(video.ad_fee || "");
                                        }}
                                        data-testid={`feed-ad-cancel-${video.id}`}
                                        className="brutal-btn-white text-xs py-2 px-3"
                                    >
                                        <X className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setEditing(true)}
                                        data-testid={`feed-ad-edit-${video.id}`}
                                        className="brutal-btn-secondary text-xs py-2 px-3"
                                    >
                                        <Edit3 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Edit advertising
                                    </button>
                                    <button
                                        onClick={onDeleteVideo}
                                        data-testid={`feed-delete-video-${video.id}`}
                                        className="brutal-btn-white text-xs py-2 px-3 hover:bg-vibrantpink hover:text-white"
                                        title="Delete video"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Delete video
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {error && (
                        <p data-testid={`feed-error-${video.id}`} className="mt-2 font-body font-semibold text-sm text-vibrantpink">
                            {error}
                        </p>
                    )}
                </div>

                {/* Right: Comments */}
                <div className="lg:col-span-5 p-5 lg:p-6 bg-cream border-t-[3px] border-ink lg:border-t-0">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-vibrantorange border-[3px] border-ink rounded-lg flex items-center justify-center shadow-brutal">
                            <MessageCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-heading font-black text-lg text-ink tracking-tight">
                            Comments <span className="text-ink/40 font-bold">({comments.length})</span>
                        </h3>
                    </div>

                    <form onSubmit={onAddComment} className="mb-4" data-testid={`feed-comment-form-${video.id}`}>
                        {(!user || user === false) && (
                            <input
                                data-testid={`feed-comment-guest-name-input-${video.id}`}
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                placeholder="Your name (optional)"
                                maxLength={24}
                                className="brutal-input mb-2 text-xs py-2"
                            />
                        )}
                        <textarea
                            data-testid={`feed-comment-input-${video.id}`}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder={user && user !== false ? "Drop a comment..." : "Comment as a guest..."}
                            rows={2}
                            maxLength={1000}
                            className="brutal-input resize-none text-sm py-2"
                        />
                        <button
                            type="submit"
                            disabled={submittingComment || !newComment.trim()}
                            className="brutal-btn-primary mt-2 w-full text-xs py-2"
                            data-testid={`feed-comment-submit-${video.id}`}
                        >
                            <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                            {submittingComment ? "Posting..." : "Post comment"}
                        </button>
                        {(!user || user === false) && (
                            <p className="mt-1.5 text-[10px] text-ink/50 font-body text-center">
                                <Link to="/login" className="font-heading font-bold underline decoration-vibrantorange decoration-[2px] underline-offset-2">
                                    Log in
                                </Link>{" "}
                                to comment under your username
                            </p>
                        )}
                    </form>

                    <div className="space-y-3" data-testid={`feed-comments-list-${video.id}`}>
                        {loadingComments ? (
                            <p className="font-body text-xs text-ink/50">Loading comments...</p>
                        ) : comments.length === 0 ? (
                            <p data-testid={`feed-comments-empty-${video.id}`} className="font-body text-sm text-ink/50 text-center py-3">
                                No comments yet. Be the first!
                            </p>
                        ) : (
                            <>
                                {visibleComments.map((c, idx) => {
                                    const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                                    const isMyComment = user && user !== false && user.id === c.user_id;
                                    return (
                                        <div key={c.id} data-testid={`feed-comment-${c.id}`} className="flex gap-2">
                                            <div className={`flex-shrink-0 w-8 h-8 ${color} border-[3px] border-ink rounded-full flex items-center justify-center`}>
                                                <User className="w-3.5 h-3.5 text-ink" strokeWidth={3} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="bg-white border-[3px] border-ink rounded-2xl rounded-tl-sm p-2.5 shadow-brutal">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="font-heading font-bold text-xs text-ink">@{c.username}</span>
                                                        {isMyComment && (
                                                            <button
                                                                onClick={() => onDeleteComment(c.id)}
                                                                data-testid={`feed-comment-delete-${c.id}`}
                                                                className="text-ink/40 hover:text-vibrantpink transition-colors"
                                                                title="Delete comment"
                                                            >
                                                                <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="font-body text-xs text-ink/90 mt-1 whitespace-pre-line break-words">
                                                        {c.text}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {comments.length > visibleComments.length && (
                                    <Link
                                        to={`/video/${video.id}`}
                                        data-testid={`feed-view-all-comments-${video.id}`}
                                        className="inline-flex items-center gap-1 font-heading font-bold text-xs uppercase tracking-widest text-vibrantorange hover:underline"
                                    >
                                        View all {comments.length} comments
                                        <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}
